// Tipos da tela Expedição. Espelham o que services/expedicao/servico.py devolve em /api/expedicao/*.
export type Canal = 'ml' | 'ml_flex' | 'shopee' | 'shein' | 'amazon' | 'tiktok' | 'site' | 'shopee_decora' | 'outro'
export type Faixa = 'atrasado' | 'hoje' | 'amanha' | 'proximos' | 'sem_prazo' | 'passado'
/** parado_tiny: separação "Outros" não embalada criada há mais de 7 dias (esquecida no Tiny); fora do dia. Também vem
 * com sem_prazo, mas a tela mostra só em "Separação parada no Tiny". */
/** aguardando_liberacao_hoje: ML retido (situação 0) com prazo hoje ou vencido. */
export type Alerta = 'atrasado' | 'cancelado_mk' | 'travado' | 'sem_prazo' | 'parado_tiny' | 'aguardando_liberacao_hoje'
/** 0 aguardando liberação (ML retido, ainda sem separação no Tiny) · 1 a separar · 4 em separação · 2 separado ·
 * 3 embalado. */
export type Situacao = 0 | 1 | 2 | 3 | 4
/** Shopee Decora (UpSeller): a etapa vem da Shopee, não do Tiny. "etiqueta" não é embalado. */
export type EtapaUpseller = 'a_processar' | 'etiqueta' | 'enviado' | 'cancelado'

export const CANAIS: Canal[] = ['ml', 'ml_flex', 'shopee', 'shein', 'amazon', 'tiktok', 'site', 'shopee_decora', 'outro']
export const ROTULO_CANAL: Record<Canal, string> = {
  ml: 'Mercado Livre', ml_flex: 'Mercado Livre Flex', shopee: 'Shopee', shein: 'Shein',
  amazon: 'Amazon', tiktok: 'TikTok', site: 'Site', shopee_decora: 'Shopee Decora (UpSeller)', outro: 'Outros',
}

/** Chave de coleta: o canal ou um subtipo dele (Amazon DBA, Site TM Logística). Nunca é o canal de um pedido. */
export type ChaveColeta = Canal | 'amazon_dba' | 'site_tm'
/** Mesma ordem do serviço (regras.ORDEM_COLETAS): o subtipo logo depois do canal. */
export const ORDEM_COLETAS: ChaveColeta[] = ['ml', 'ml_flex', 'shopee', 'shein', 'amazon', 'amazon_dba', 'tiktok', 'site', 'site_tm', 'shopee_decora', 'outro']
export const ROTULO_COLETA: Record<ChaveColeta, string> = { ...ROTULO_CANAL, amazon_dba: 'Amazon DBA', site_tm: 'Site TM Logística' }

export type Pedido = {
  id: string
  id_separacao: string
  nota: string | null
  pedido_marketplace: string | null
  canal: Canal
  destinatario: string
  id_forma_envio: string | null
  forma_envio: string | null
  situacao: Situacao
  data_criacao: string | null // YYYY-MM-DD
  data_checkout: string | null // YYYY-MM-DD
  embalador_id: string | null
  embalado_em: string | null // ISO UTC
  em_separacao_desde: string | null // ISO UTC
  /** ISO UTC; null = sem prazo. Canal fora de `canais_com_prazo` (flag desligada ou fonte com erro) sempre vem com
   * prazo e prazo_fonte null: nada de prazo velho na tela. status_mk/status_mk_bruto só vêm null com a flag
   * desligada (como na F1); com a fonte caída o "cancelado" confirmado fica. */
  prazo: string | null
  prazo_fonte: string | null
  status_mk: 'a_enviar' | 'cancelado' | 'enviado' | null
  status_mk_bruto: string | null
  ml?: { order_id: string; shipment_id: string; logistic_type: string }
  /** Coleta do pedido (calculada pelo serviço): canal_coleta do Site, amazon_dba para Amazon DBA, senão o canal.
   * Ausente em serviço antigo (= canal). */
  coleta?: ChaveColeta
  /** Amazon: onsite (AFN) | dba (MFN). */
  tipo?: 'onsite' | 'dba' | null
  /** Site: site (Correios/Retirada) | site_tm (TM Logística). */
  canal_coleta?: 'site' | 'site_tm' | null
  /** nº do envio: shipment_id do ML ou o pacote do marketplace; null = sem envio conhecido. */
  envio: string | null
  excluido: boolean
  ativo: boolean
  historico: { situacao: number; visto_em: string }[]
  lido_em: string | null
  /** Origem do doc: separação do Tiny (ausente), "ml_api" (ML retido, pré-separação) ou "shopee_api" (Decora). */
  origem?: string | null
  /** ML retido que o ML já liberou e o Tiny ainda não criou a separação. */
  liberado_ml?: boolean | null
  /** Shopee Decora: etapa no UpSeller (a situação do Tiny não vale para ela). */
  etapa_upseller?: EtapaUpseller | null
  /** Shopee Decora cancelada: a etapa de antes do cancelamento ("etiqueta" = conferir no UpSeller). */
  etapa_upseller_antes?: EtapaUpseller | null
  enviado_em?: string | null
  // calculados pelo serviço
  faixa: Faixa
  alertas: Alerta[]
  no_dia: boolean
  embalador_nome: string
  canal_rotulo: string
}

export type Fonte = { fonte: string; ok: boolean; lido_em: string | null; erro: string | null; lidos: number }

export type DiaDeHoje = {
  data: string
  dia_util: boolean
  total: number
  embalados: number
  faltam: number
  pct: number
  sem_prazo: number
  /** chaves '0' | '1' | '4' | '2' | '3' e, da Shopee Decora, 'up_a_processar' | 'up_etiqueta' | 'up_enviado' */
  por_situacao: Record<string, number>
  ritmo_hora: number
  precisa_hora: number | null
  previsao_fim: string | null
  ficariam: number
  encerrado: boolean
  acumulado_hora: { hora: string; embalados: number }[]
  amanha: number
  proximos: number
  /** Ritmo até a coleta de cada canal com pedido no dia, por hora crescente. */
  por_coleta: LinhaColeta[]
  /** Próxima coleta que ainda não passou e tem pendência; null = nenhuma. */
  proxima_coleta: { canal: ChaveColeta; hora: string } | null
  /** Pendentes (não embalados) dos canais sem prazo de API ligado; fora do total do dia. */
  sem_prazo_por_canal: Record<string, number>
  /** Embalados do dia sem transição observada hoje (ou antes do início): fora da curva hora a hora. */
  saldo_inicial: number
  /** "HH:MM": a linha ideal do gráfico termina aqui (última coleta com pendência; sem pendência = fim do expediente). */
  fim_ideal: string
}

/** Fonte da hora de coleta: agenda da API (ML), tabela fixa ou reserva quando a agenda falhou. */
export type FonteColeta = 'api' | 'tabela' | 'estimativa'
export type Coleta = { hora: string; fonte: FonteColeta; janela: string | null; corte: string | null }

export type LinhaColeta = Coleta & {
  /** chave da coleta (canal ou amazon_dba/site_tm). */
  canal: ChaveColeta
  rotulo: string
  total: number
  embalados: number
  faltam: number
  /** null depois que a coleta passou. */
  precisa_hora: number | null
  passou: boolean
  ficaram: number
}

export type PontoSerie = { dia: string; util: boolean; total: number; por_canal: Partial<Record<Canal, number>> }
export type PessoaRanking = { embalador_id: string | null; nome: string; total: number }

/** % embalado no prazo do último dia útil: pedidos com prazo nesse dia (ativos e inativos). */
export type Ontem = { data: string; total: number; no_prazo: number; pct: number }

export type DadosExpedicao = {
  gerado_em: string
  agora_sp: string
  expediente: { inicio: string; fim: string }
  fontes: Fonte[]
  pedidos: Pedido[]
  coletas: Partial<Record<ChaveColeta, Coleta>>
  /** Canais cujo prazo entra no dia (ML e Flex sempre; Shopee/Shein com a flag e a fonte ok). */
  canais_com_prazo: Canal[]
  /** Embalados (situação 3) ativos cancelados no marketplace: retirar da carga. Equipe inteira (não segue filtro). */
  cancelados_embalados: Pedido[]
  /** Separações "Outros" paradas no Tiny há mais de 7 dias (alerta parado_tiny); fora do dia. Ausente em serviço antigo. */
  parados_tiny?: Pedido[]
  dia: DiaDeHoje
  ontem: Ontem
  serie_30: PontoSerie[]
  por_pessoa: PessoaRanking[]
}

/** Abre a gaveta com a lista de pedidos de um número da tela. Todo número de pedidos chama isto. */
export type AbrirLista = (titulo: string, pedidos: Pedido[]) => void
